/**
 * search-share — tiny theme/strings bridge between ChatWidget and the separate
 * <{brand}-search> element (Task 2). The two custom elements are independent
 * (search-box must NOT import chat-widget.ts — that would risk an import cycle
 * and drag the whole chat bundle into a page that only wants the search box);
 * this module is the only channel between them.
 *
 * Mirrors the active-instance registry style in public-api.ts, but pushed the
 * other way: ChatWidget publishes a snapshot once it knows the tenant's theme +
 * i18n strings (post-bootstrap), and any number of search boxes subscribe. A
 * late subscriber (search box mounted/connected after the chat already booted)
 * is replayed the current snapshot immediately instead of missing it.
 */

export interface SearchContext {
  /** Same color the chat widget uses for its primary/CTA button (theme.primary_color). */
  accent: string;
  /** Text color for that surface — owner's explicit primary_ink, else auto-contrast
   *  (mirrors ChatWidget.applyTheme's --primary-ink; computed independently in
   *  ensureBooted since search-box.ts must not import chat-widget.ts). '' = unknown yet. */
  accentInk: string;
  placeholder: string;
  buttonLabel: string;
}

type Listener = (ctx: SearchContext) => void;

let current: SearchContext | null = null;
const listeners = new Set<Listener>();

/**
 * Called by ChatWidget right after a successful bootstrap. Each listener runs
 * isolated — a throwing subscriber (buggy search box) must not stop the other
 * subscribers from being notified, and must never propagate back into
 * ensureBooted's try/catch (which would wrongly flip a successful bootstrap
 * into a bootError).
 */
export function publishSearchContext(ctx: SearchContext): void {
  current = ctx;
  for (const cb of listeners) {
    try {
      cb(ctx);
    } catch {
      // isolated — see doc above
    }
  }
}

/**
 * Subscribe to context updates. Returns an unsubscribe function. Invokes `cb`
 * immediately with the current snapshot when one already exists (late
 * subscriber — see module doc); does nothing yet if no chat widget has
 * published (pre-bootstrap), in which case the search box keeps its own
 * neutral defaults until publish fires. The replay call is isolated the same
 * way as publishSearchContext's notify loop (see there).
 */
export function subscribeSearchContext(cb: Listener): () => void {
  listeners.add(cb);
  if (current !== null) {
    try {
      cb(current);
    } catch {
      // isolated — see publishSearchContext doc
    }
  }
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only: reset module state between specs. */
export function __resetSearchShareForTests(): void {
  current = null;
  listeners.clear();
}
